/**
 * Rich Text Component
 */

import { v4 as uuidv4 } from 'uuid'

import { VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatFromVariableSelect,
  formatHorizontalAlignmentFormat,
  formatSpacingFormat,
  formatTextAlignmentFormat,
  formatWidthFormat,
} from '../../format-utils'
import type {
  AppBreakpoint,
  HorizontalAlignment,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  VariableSelectValue,
} from '../../types'
import {
  createResponsiveValue,
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseWidthFormat,
} from '../../parser-utils'
import richTextTemplate from './rich-text.template.json'

export interface IRichTextComponentSettings {
  // Data settings
  'rich-text-data': VariableSelectValue

  // Design settings
  'rich-text-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'rich-text-design-h-align': ResponsiveValue<TextAlignment>
  'rich-text-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'rich-text-design-group-text-color': any
  'rich-text-design-h1-color': any
  'rich-text-design-h1-color-solid': any
  'rich-text-design-h1-color-start-gradient': any
  'rich-text-design-h1-color-end-gradient': any
  'rich-text-design-h1-color-gradient-direction': any
  'rich-text-design-scheme': any
  'rich-text-design-h2-color': any
  'rich-text-design-h2-color-solid': any
  'rich-text-design-h2-color-start-gradient': any
  'rich-text-design-h2-color-end-gradient': any
  'rich-text-design-h2-color-gradient-direction': any
  'rich-text-design-h3-color': any
  'rich-text-design-h3-color-solid': any
  'rich-text-design-h3-color-start-gradient': any
  'rich-text-design-h3-color-end-gradient': any
  'rich-text-design-h3-color-gradient-direction': any
  'rich-text-design-h4-color': any
  'rich-text-design-h4-color-solid': any
  'rich-text-design-h4-color-start-gradient': any
  'rich-text-design-h4-color-end-gradient': any
  'rich-text-design-h4-color-gradient-direction': any
  'rich-text-design-h5-color': any
  'rich-text-design-h5-color-solid': any
  'rich-text-design-h5-color-start-gradient': any
  'rich-text-design-h5-color-end-gradient': any
  'rich-text-design-h5-color-gradient-direction': any
  'rich-text-design-h6-color': any
  'rich-text-design-h6-color-solid': any
  'rich-text-design-h6-color-start-gradient': any
  'rich-text-design-h6-color-end-gradient': any
  'rich-text-design-h6-color-gradient-direction': any
  'rich-text-design-p-color': any
  'rich-text-design-p-color-solid': any
  'rich-text-design-p-color-start-gradient': any
  'rich-text-design-p-color-end-gradient': any
  'rich-text-design-p-color-gradient-direction': any
  'rich-text-design-ol-color': any
  'rich-text-design-ol-color-solid': any
  'rich-text-design-ol-color-start-gradient': any
  'rich-text-design-ol-color-end-gradient': any
  'rich-text-design-ol-color-gradient-direction': any
  'rich-text-design-ul-color': any
  'rich-text-design-ul-color-solid': any
  'rich-text-design-ul-color-start-gradient': any
  'rich-text-design-ul-color-end-gradient': any
  'rich-text-design-ul-color-gradient-direction': any
}

export interface IRichTextComponent extends IBuilderComponent {
  componentId: 'rich-text'
  groupId: 'text'
  settings: IRichTextComponentSettings
}

export interface AiRichTextInput {
  content: string // Markdown content
  width?: string // Format: "w:100%" or "w:800px" or "w:100% de:w:800px"
  margin?: string // Format: "m:2rem" or "mb:16px" or "m:2rem ta:m:3rem"
  align?: string // Format: "h:center" or "h:start ta:h:center"
  h1Color?: string // Format: "solid:primary-40" or "gradient:primary-40|primary-80:to-right" or "adaptive"
  h2Color?: string
  h3Color?: string
  h4Color?: string
  h5Color?: string
  h6Color?: string
  pColor?: string // Paragraph color
  olColor?: string // Ordered list color
  ulColor?: string // Unordered list color
}

const RICH_TEXT_DEFAULT_TEMPLATE = richTextTemplate as unknown as IRichTextComponentSettings

export class RichTextComp {
  getTemplate(): IRichTextComponentSettings {
    return RICH_TEXT_DEFAULT_TEMPLATE
  }

  /**
   * format rich text component settings to AI rich text input
   */
  format(settings: IRichTextComponentSettings): AiRichTextInput {
    const result: AiRichTextInput = {
      content: '',
    }

    // Data - Rich text content (from VariableSelectValue)
    // Rich text uses VariableSelectValue with RichText flag, which formats to "rc:value"
    // But for rich-text content, we want the raw markdown value
    const richTextData = settings['rich-text-data'] as any
    if (richTextData && richTextData.value) {
      result.content = richTextData.value
    } else {
      const contentValue = formatFromVariableSelect(richTextData)
      if (contentValue) {
        // Extract the value from format string (e.g., "rc:markdown" -> "markdown" or "text:Hello" -> "Hello")
        if (contentValue.startsWith('rc:')) {
          result.content = contentValue.substring(3)
        } else if (contentValue.startsWith('text:')) {
          result.content = contentValue.substring(5)
        } else {
          result.content = contentValue
        }
      }
    }

    // Design - Width (handle OverflowSettings)
    const width = formatWidthFormat(settings['rich-text-design-width'])
    if (width) {
      result.width = width
    }

    // Design - Margin (handle OverflowSettings)
    const margin = formatSpacingFormat(settings['rich-text-design-margin'])
    if (margin) {
      result.margin = margin
    }

    // Design - Alignment (HorizontalAlignment)
    const richTextAlign = settings['rich-text-design-h-align'] as ResponsiveValue<HorizontalAlignment>
    if (richTextAlign) {
      result.align = formatHorizontalAlignmentFormat(richTextAlign)
    }

    // Helper function to format color for a specific element type
    const formatElementColor = (prefix: string): string | undefined => {
      return formatColorFormat({
        colorType: settings[`${prefix}-color` as keyof IRichTextComponentSettings] as any,
        colorSolid: settings[`${prefix}-color-solid` as keyof IRichTextComponentSettings] as any,
        colorStartGradient: settings[`${prefix}-color-start-gradient` as keyof IRichTextComponentSettings] as any,
        colorEndGradient: settings[`${prefix}-color-end-gradient` as keyof IRichTextComponentSettings] as any,
        colorGradientDirection: settings[
          `${prefix}-color-gradient-direction` as keyof IRichTextComponentSettings
        ] as any,
      })
    }

    // Design - Colors for different text elements
    const h1Color = formatElementColor('rich-text-design-h1')
    if (h1Color) {
      result.h1Color = h1Color
    }

    const h2Color = formatElementColor('rich-text-design-h2')
    if (h2Color) {
      result.h2Color = h2Color
    }

    const h3Color = formatElementColor('rich-text-design-h3')
    if (h3Color) {
      result.h3Color = h3Color
    }

    const h4Color = formatElementColor('rich-text-design-h4')
    if (h4Color) {
      result.h4Color = h4Color
    }

    const h5Color = formatElementColor('rich-text-design-h5')
    if (h5Color) {
      result.h5Color = h5Color
    }

    const h6Color = formatElementColor('rich-text-design-h6')
    if (h6Color) {
      result.h6Color = h6Color
    }

    const pColor = formatElementColor('rich-text-design-p')
    if (pColor) {
      result.pColor = pColor
    }

    const olColor = formatElementColor('rich-text-design-ol')
    if (olColor) {
      result.olColor = olColor
    }

    const ulColor = formatElementColor('rich-text-design-ul')
    if (ulColor) {
      result.ulColor = ulColor
    }

    return result
  }

  /**
   * parse AI rich text input to rich text component settings
   */
  parse(input: AiRichTextInput): IRichTextComponentSettings {
    const result = deepMerge(RICH_TEXT_DEFAULT_TEMPLATE, {})

    // Data - Rich text content (markdown)
    if (input.content) {
      result['rich-text-data'] = {
        id: uuidv4(),
        value: input.content,
        flag: VariableSelectFeatureFlag.CustomText,
      }
    }

    // Design - Width
    if (input.width) {
      const widthParsed = parseWidthFormat(input.width)
      result['rich-text-design-width'] = {
        ...widthParsed,
        overflowSetting: result['rich-text-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      const marginParsed = parseSpacingFormat(input.margin)
      result['rich-text-design-margin'] = {
        ...marginParsed,
        overflowSetting: result['rich-text-design-margin'].overflowSetting,
      }
    }

    // Design - Alignment
    if (input.align) {
      const alignmentParsed = parseAlignmentFormat(input.align)
      if (alignmentParsed.hAlign) {
        // Map ComponentAlignment to TextAlignment
        // Handle 'parent' specially - it should map to 'parent' in TextAlignment
        const mappedTextAlign = Object.fromEntries(
          Object.entries(alignmentParsed.hAlign).map(([key, value]) => {
            let textAlign: TextAlignment
            if (value === 'parent')
              textAlign = 'parent' as any // TextAlignment can include 'parent'
            else if (value === 'start') textAlign = 'left'
            else if (value === 'end') textAlign = 'right'
            else textAlign = 'center'
            return [key, textAlign]
          })
        ) as ResponsiveValue<TextAlignment>
        result['rich-text-design-h-align'] = mappedTextAlign
      }
      if (alignmentParsed.textAlign) {
        result['rich-text-design-h-align'] = alignmentParsed.textAlign
      }
    }

    // Helper function to apply color to a specific element type
    const applyColor = (prefix: string, color: string) => {
      const colorParsed = parseColorFormat(color)
      result[`${prefix}-color` as keyof IRichTextComponentSettings] = colorParsed.colorType
      result[`${prefix}-color-solid` as keyof IRichTextComponentSettings] = colorParsed.colorSolid
      result[`${prefix}-color-start-gradient` as keyof IRichTextComponentSettings] = colorParsed.colorStartGradient
      result[`${prefix}-color-end-gradient` as keyof IRichTextComponentSettings] = colorParsed.colorEndGradient
      result[`${prefix}-color-gradient-direction` as keyof IRichTextComponentSettings] =
        colorParsed.colorGradientDirection
    }

    // Design - Colors for different text elements
    if (input.h1Color) {
      applyColor('rich-text-design-h1', input.h1Color)
    }
    if (input.h2Color) {
      applyColor('rich-text-design-h2', input.h2Color)
    }
    if (input.h3Color) {
      applyColor('rich-text-design-h3', input.h3Color)
    }
    if (input.h4Color) {
      applyColor('rich-text-design-h4', input.h4Color)
    }
    if (input.h5Color) {
      applyColor('rich-text-design-h5', input.h5Color)
    }
    if (input.h6Color) {
      applyColor('rich-text-design-h6', input.h6Color)
    }
    if (input.pColor) {
      applyColor('rich-text-design-p', input.pColor)
    }
    if (input.olColor) {
      applyColor('rich-text-design-ol', input.olColor)
    }
    if (input.ulColor) {
      applyColor('rich-text-design-ul', input.ulColor)
    }

    return result
  }
}
